import { useEffect, useState, type ReactElement } from "react";
import type { FeatureScreenProps } from "../../../web/src/features/contract.js";
import { PageHelp } from "../../../web/src/layout/PageHelp.js";

interface QueueItem {
  applicationId: string;
  status: string;
  submittedAt?: number;
  durationSec?: number;
  language?: string;
  phone: string;
  claimedBy?: string;
  /** Automated review-flags badge: "review" | "clear" | null (not yet computed). */
  flagSummary?: string | null;
}

/** Automated review flags (feat/admissions-review-flags) as returned on the detail surface. */
interface ApplicationFlags {
  liveness?: { status: string; matched: string[]; missing: string[]; required: number; total: number; passed: boolean };
  moderation?: { status: string; labels: Array<{ name: string; parent: string; confidence: number; firstTimestampMs: number }> };
  keywords?: { status: string; matches: Array<{ id: string; label: string; phrases: string[] }> };
  silence?: { status: string; speakingRatio: number | null; words: number | null; passed: boolean };
  origin?: { status: string; creationTimeMs?: number; challengeIssuedAtMs?: number; deltaSec?: number };
  summary: "clear" | "review";
}

interface Detail {
  applicationId: string;
  status: string;
  phone: string;
  consentVersion?: number;
  trainingConsent?: boolean;
  transcript?: string | null;
  transcriptStatus?: string;
  language?: string;
  quality?: Record<string, number | string | null> | null;
  history?: Array<{ at: number; reviewerId: string; decision: string; isSecondReview?: boolean }>;
  modelScore?: number;
  modelVersion?: string;
  flags?: ApplicationFlags | null;
}

interface FlagRate {
  flagged: number;
  total: number;
  rate: number;
}

interface Stats {
  queueDepth: number;
  decisionsByCategory: Record<string, number>;
  reviewerKappa: number | null;
  doubleReviewed: number;
  /** Per language, per check: how often the check flagged (fairness view). */
  flagRatesByLanguage?: Record<string, Record<string, FlagRate>>;
}

const FLAG_RATE_CHECKS: Array<[string, string]> = [
  ["liveness", "Check words"],
  ["moderation", "Video content"],
  ["keywords", "Keywords"],
  ["silence", "Silence"],
  ["origin", "Upload timing"],
  ["summary", "Any flag"],
];

/** Fairness view: flag rate per check for each language, so a check that fires more for one group is visible. */
function FlagRatesTable({ rates }: { rates: Record<string, Record<string, FlagRate>> }): ReactElement {
  const languages = Object.keys(rates).sort();
  if (languages.length === 0) return <p>No flagged checks evaluated yet.</p>;
  const cell = (r: FlagRate | undefined): string => (r && r.total > 0 ? `${Math.round(r.rate * 100)}% (${r.flagged}/${r.total})` : "n/a");
  return (
    <table>
      <caption>Flag rate by language (share of evaluated applications each check flagged)</caption>
      <thead>
        <tr>
          <th scope="col">Language</th>
          {FLAG_RATE_CHECKS.map(([, label]) => <th key={label} scope="col">{label}</th>)}
        </tr>
      </thead>
      <tbody>
        {languages.map((lang) => (
          <tr key={lang}>
            <th scope="row">{lang}</th>
            {FLAG_RATE_CHECKS.map(([id]) => <td key={id}>{cell(rates[lang][id])}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const SLA_HOURS = 48;
const PRIMARY_REASONS = [
  "GENUINE_ALIGNED", "UNCLEAR_INTENT", "LOW_EFFORT", "COMMERCIAL", "SOLICITATION", "HOSTILE",
  "APPEARS_UNDER_18", "NOT_THE_APPLICANT", "SCRIPTED", "SUSPECTED_SYNTHETIC", "TECHNICAL_UNUSABLE",
];

/** Plain-language meaning of each queue status. */
const STATUS_HELP: Record<string, string> = {
  SUBMITTED: "New applications nobody has picked up yet.",
  IN_REVIEW: "Applications a reviewer has claimed and is working on.",
  NEEDS_RESUBMISSION: "The applicant was asked to record their video again.",
  APPROVED: "Accepted. The applicant was sent an invite.",
  REJECTED: "Turned down. The applicant can't apply again for a while.",
  ALREADY_MEMBER: "The phone number already belongs to a member, so no invite was needed.",
};

/** Plain-language meaning of each decision. */
const DECISION_HELP: Record<string, string> = {
  APPROVE: "Accept. The applicant gets a text with a one-time invite to join.",
  REJECT: "Turn down. The applicant gets a text and can't apply again for the cooldown period. Notes are required.",
  REQUEST_RERECORD: "Ask the applicant to record a new video (e.g. bad sound or unclear). They see the request when they check their status in the app; no text is sent.",
};

/** Plain-language meaning of each primary reason. */
const REASON_HELP: Record<string, string> = {
  GENUINE_ALIGNED: "Sincere, and a good fit for the community.",
  UNCLEAR_INTENT: "It isn't clear why they want to join.",
  LOW_EFFORT: "Very little effort, e.g. a few words or no real answer.",
  COMMERCIAL: "Promoting a business, product or service.",
  SOLICITATION: "Looking to sell, recruit or ask for money or contacts.",
  HOSTILE: "Rude, aggressive or offensive.",
  APPEARS_UNDER_18: "The applicant looks or says they are under 18.",
  NOT_THE_APPLICANT: "The person in the video doesn't seem to be the person applying.",
  SCRIPTED: "Reading a script or clearly rehearsed rather than speaking for themselves.",
  SUSPECTED_SYNTHETIC: "The video looks computer-generated or edited.",
  TECHNICAL_UNUSABLE: "Can't be judged: no sound, black screen, cut off, etc.",
};

function slaBreached(submittedAt?: number): boolean {
  return typeof submittedAt === "number" && Date.now() - submittedAt > SLA_HOURS * 3_600_000;
}

/** Admissions review workspace: Queue, Review (video + rubric), Stats. Rendered via the feature
 *  screen registry. Model score is shown only when the server includes it (view-model-score). */
export function AdmissionsScreen({ actions, call }: FeatureScreenProps): ReactElement {
  const has = (id: string): boolean => actions.some((a) => a.id === id);
  const canReview = has("queue");
  const canPrivacy = has(PRIVACY_SURFACE);
  // A privacy officer has only the privacy action, so their screen opens on (and shows only) Privacy.
  const [tab, setTab] = useState<"queue" | "review" | "stats" | "privacy">(canReview ? "queue" : "privacy");
  const [statusFilter, setStatusFilter] = useState("SUBMITTED");
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [items, setItems] = useState<QueueItem[]>([]);
  const [selected, setSelected] = useState<Detail | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [revealedPhone, setRevealedPhone] = useState<string | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function loadQueue(): Promise<void> {
    setError(null);
    try {
      const res = (await call("queue", "GET", { status: statusFilter, limit: 50, flagged: flaggedOnly })) as { items: QueueItem[] };
      setItems(res.items ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load queue");
    }
  }

  useEffect(() => {
    if (tab === "queue" && canReview) void loadQueue();
    if (tab === "stats" && has("stats")) void call("stats", "GET").then((s) => setStats(s as Stats)).catch(() => setError("Failed to load stats"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, statusFilter, flaggedOnly]);

  async function open(id: string): Promise<void> {
    setError(null);
    setRevealedPhone(null);
    setVideoUrl(null);
    try {
      const d = (await call("detail", "GET", { applicationId: id })) as Detail;
      setSelected(d);
      setTab("review");
      try {
        const m = (await call("media", "POST", { applicationId: id })) as { url: string };
        setVideoUrl(m.url);
      } catch { /* media optional */ }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to open application");
    }
  }

  async function claim(id: string): Promise<void> {
    try { await call("claim", "POST", { applicationId: id }); await loadQueue(); }
    catch (e) { setError(e instanceof Error ? e.message : "Claim failed"); }
  }

  async function reveal(): Promise<void> {
    if (!selected) return;
    try {
      const r = (await call("reveal-phone", "POST", { applicationId: selected.applicationId })) as { phone: string };
      setRevealedPhone(r.phone);
    } catch (e) { setError(e instanceof Error ? e.message : "Reveal failed"); }
  }

  return (
    <section aria-labelledby="adm-title">
      <h1 id="adm-title">Admissions</h1>
      <PageHelp>
        <p>
          People without an invite can ask for one by recording a short video. Here you watch those videos and decide who
          gets in.
        </p>
        <ul>
          <li><strong>Queue</strong>: the list of applications. Claim one so nobody else works on it at the same time.</li>
          <li><strong>Review</strong>: watch the video, read the transcript and checks, then make your decision.</li>
          <li><strong>Stats</strong>: how the queue and reviewers are doing (senior reviewers and owners only).</li>
          <li><strong>Privacy</strong>: act on an emailed request to stop using an applicant&apos;s video for training (privacy officers and owners only).</li>
        </ul>
        <p>Try to decide every application within {SLA_HOURS} hours of it arriving.</p>
      </PageHelp>
      <nav aria-label="Admissions sections">
        {canReview ? <button type="button" aria-current={tab === "queue"} onClick={() => setTab("queue")}>Queue</button> : null}
        {canReview ? <button type="button" aria-current={tab === "review"} onClick={() => setTab("review")} disabled={!selected}>Review</button> : null}
        {has("stats") ? <button type="button" aria-current={tab === "stats"} onClick={() => setTab("stats")}>Stats</button> : null}
        {canPrivacy ? <button type="button" aria-current={tab === "privacy"} onClick={() => setTab("privacy")}>Privacy</button> : null}
      </nav>
      {error ? <p role="alert">{error}</p> : null}
      {tab === "privacy" && canPrivacy ? <PrivacyRequestForm call={call} /> : null}

      {tab === "queue" ? (
        <div>
          <label htmlFor="status-filter">Status</label>
          <select id="status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-describedby="status-hint">
            {["SUBMITTED", "IN_REVIEW", "NEEDS_RESUBMISSION", "APPROVED", "REJECTED", "ALREADY_MEMBER"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <p id="status-hint" className="field-hint">Showing: {STATUS_HELP[statusFilter] ?? statusFilter}</p>
          <label htmlFor="flagged-only">
            <input id="flagged-only" type="checkbox" checked={flaggedOnly} onChange={(e) => setFlaggedOnly(e.target.checked)} aria-describedby="flagged-hint" />
            {" "}Flagged for review only
          </label>
          <p id="flagged-hint" className="field-hint">Only show applications where an automatic check spotted something worth a closer look.</p>
          <p className="field-hint">
            Click an ID to open it. Phone numbers are hidden. Flags: 🚩 review = a check spotted something, clear = nothing
            spotted, — = checks still running. SLA shows ⚠ when an application has waited more than {SLA_HOURS} hours.
          </p>
          <table>
            <caption>Application queue</caption>
            <thead><tr><th scope="col">ID</th><th scope="col">Phone</th><th scope="col">Status</th><th scope="col">Flags</th><th scope="col">SLA</th><th scope="col">Actions</th></tr></thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.applicationId}>
                  <td><button type="button" onClick={() => open(it.applicationId)}>{it.applicationId}</button></td>
                  <td>{it.phone}</td>
                  <td>{it.status}</td>
                  <td><FlagBadge summary={it.flagSummary} /></td>
                  <td>{slaBreached(it.submittedAt) ? <span role="status">⚠ breached</span> : "ok"}</td>
                  <td>{has("claim") ? <button type="button" onClick={() => claim(it.applicationId)}>Claim</button> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {tab === "review" && selected ? (
        <ReviewPanel detail={selected} videoUrl={videoUrl} revealedPhone={revealedPhone} onReveal={reveal} canReveal={has("reveal-phone")} canDecide={has("decide")} onDecide={async (form) => {
          try { await call("decide", "POST", { applicationId: selected.applicationId, ...form }); await loadQueue(); setTab("queue"); setSelected(null); }
          catch (e) { setError(e instanceof Error ? e.message : "Decision failed"); }
        }} />
      ) : null}

      {tab === "stats" && stats ? (
        <div>
          <h2>Stats</h2>
          <p>Queue depth: <strong>{stats.queueDepth}</strong></p>
          <p className="field-hint">Applications waiting for a decision.</p>
          <p>Reviewer agreement (Cohen&apos;s κ): <strong>{stats.reviewerKappa === null ? "n/a" : stats.reviewerKappa.toFixed(2)}</strong> over {stats.doubleReviewed} double-reviewed</p>
          <p className="field-hint">
            Some applications are reviewed by two people. This shows how often they agree: 1 = always, 0 = no better than
            chance. Below about 0.6 means reviewers judge differently and may need clearer guidance.
          </p>
          <h3>Decisions by category</h3>
          <p className="field-hint">How many decisions were made for each reason.</p>
          <ul>{Object.entries(stats.decisionsByCategory).map(([k, v]) => <li key={k}>{k}: {v}</li>)}</ul>
          {stats.flagRatesByLanguage ? (
            <>
              <h3>Automated checks by language</h3>
              <p className="field-hint">
                How often each automatic check flags applications, per language. If one language is flagged much more than
                others, that check may be unfair to those applicants.
              </p>
              <FlagRatesTable rates={stats.flagRatesByLanguage} />
            </>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function ReviewPanel(props: {
  detail: Detail;
  videoUrl: string | null;
  revealedPhone: string | null;
  canReveal: boolean;
  canDecide: boolean;
  onReveal: () => void;
  onDecide: (form: Record<string, unknown>) => Promise<void>;
}): ReactElement {
  const { detail, videoUrl, revealedPhone } = props;
  const [decision, setDecision] = useState("APPROVE");
  const [primaryReason, setPrimaryReason] = useState(PRIMARY_REASONS[0]);
  const [confidence, setConfidence] = useState(3);
  const [notes, setNotes] = useState("");
  const [respectfulTone, setRespectfulTone] = useState(3);
  const [clearIntentions, setClearIntentions] = useState(3);
  const [confirming, setConfirming] = useState(false);

  const rubric = {
    intentClarity: 3,
    authenticity: 3,
    effort: 3,
    communicationClarity: 3,
    guidelineAlignment: 3,
    respectfulTone,
    clearIntentions,
  };

  function submit(): void {
    void props.onDecide({ decision, primaryReason, confidence, notes: notes || undefined, ...rubric });
    setConfirming(false);
  }

  return (
    <div>
      <h2>Review {detail.applicationId}</h2>
      <p className="field-hint">
        Watch the video first, then use the transcript and automatic checks to help you. The checks are only hints; the
        decision is always yours.
      </p>
      <div>
        {videoUrl ? (
          <video controls aria-label="Application video" src={videoUrl} width={320} />
        ) : <p>Video unavailable</p>}
      </div>
      <section aria-label="Transcript">
        <h3>Transcript</h3>
        <p className="field-hint">An automatic written version of what the applicant said. It can contain mistakes.</p>
        <p>
          Status: <strong>{detail.transcriptStatus ?? "NONE"}</strong>
          {detail.language ? <> · Language: <strong>{detail.language}</strong></> : null}
        </p>
        <p>{detail.transcript ?? "(transcript not available yet)"}</p>
      </section>
      <section aria-label="Quality metrics">
        <h3>Quality metrics</h3>
        <p className="field-hint">Measured facts about the recording, such as length and how much of it is speech.</p>
        {detail.quality && Object.keys(detail.quality).length > 0 ? (
          <ul>
            {Object.entries(detail.quality).map(([k, v]) => (
              <li key={k}>{k}: {v === null || v === undefined ? "n/a" : String(v)}</li>
            ))}
          </ul>
        ) : (
          <p>(no computed quality metrics yet)</p>
        )}
        <p>
          <small>Lighting is reviewer-entered only; it is not computed (no frame decoding / no face analysis, D16).</small>
        </p>
      </section>
      <FlagsPanel flags={detail.flags} />
      <p>
        Phone: <span>{revealedPhone ?? detail.phone}</span>{" "}
        {props.canReveal && !revealedPhone ? <button type="button" onClick={props.onReveal} aria-describedby="reveal-hint">Reveal phone</button> : null}
      </p>
      {props.canReveal && !revealedPhone ? (
        <p id="reveal-hint" className="field-hint">Shows the full number. Only do this when you need it; every reveal is recorded with your name.</p>
      ) : null}
      <p>Consent version: {detail.consentVersion ?? "n/a"} · Training consent: {detail.trainingConsent ? "yes" : "no"}</p>
      <p className="field-hint">
        Training consent = whether the applicant agreed that their video may be used to train Admissions Assist. It does
        not affect your decision.
      </p>
      {typeof detail.modelScore === "number" ? (
        <>
          <p>Model score (Admissions Assist {detail.modelVersion}): <strong>{detail.modelScore.toFixed(3)}</strong></p>
          <p className="field-hint">
            From 0 to 1: how likely the model thinks it is that reviewers would approve. It is a hint only; judge the
            video yourself.
          </p>
        </>
      ) : null}
      <section aria-label="History">
        <h3>History</h3>
        <p className="field-hint">Earlier decisions on this application, newest last. (2nd) marks a second review.</p>
        <ul>{(detail.history ?? []).map((h, i) => <li key={i}>{new Date(h.at).toISOString()} — {h.reviewerId}: {h.decision}{h.isSecondReview ? " (2nd)" : ""}</li>)}</ul>
      </section>

      {props.canDecide ? (
        <form onSubmit={(e) => { e.preventDefault(); setConfirming(true); }}>
          <fieldset>
            <legend>Decision</legend>
            <label htmlFor="decision">Decision</label>
            <select id="decision" value={decision} onChange={(e) => setDecision(e.target.value)} aria-describedby="decision-hint">
              {["APPROVE", "REJECT", "REQUEST_RERECORD"].map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <p id="decision-hint" className="field-hint">{DECISION_HELP[decision]}</p>
            <label htmlFor="primaryReason">Primary reason</label>
            <select id="primaryReason" value={primaryReason} onChange={(e) => setPrimaryReason(e.target.value)} aria-describedby="reason-hint">
              {PRIMARY_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <p id="reason-hint" className="field-hint">The main reason for your decision: {REASON_HELP[primaryReason]}</p>
            <label htmlFor="confidence">Confidence (1-5)</label>
            <input id="confidence" type="number" min={1} max={5} value={confidence} onChange={(e) => setConfidence(Number(e.target.value))} aria-describedby="confidence-hint" />
            <p id="confidence-hint" className="field-hint">How sure you are. 1 = a guess, 5 = completely sure.</p>
            <label htmlFor="respectfulTone">Respectful tone (1-5)</label>
            <input id="respectfulTone" type="number" min={1} max={5} value={respectfulTone} onChange={(e) => setRespectfulTone(Number(e.target.value))} aria-describedby="tone-hint" />
            <p id="tone-hint" className="field-hint">How respectful the applicant came across. 1 = rude, 5 = very respectful.</p>
            <label htmlFor="clearIntentions">Clear intentions (1-5)</label>
            <input id="clearIntentions" type="number" min={1} max={5} value={clearIntentions} onChange={(e) => setClearIntentions(Number(e.target.value))} aria-describedby="intent-hint" />
            <p id="intent-hint" className="field-hint">How clearly they explained why they want to join. 1 = not at all, 5 = very clear.</p>
            <label htmlFor="notes">Notes {decision === "REJECT" ? "(required)" : ""}</label>
            <textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} aria-describedby="notes-hint" />
            <p id="notes-hint" className="field-hint">
              Anything another reviewer should know. Required when rejecting. Not shown to the applicant.
            </p>
          </fieldset>
          <button type="submit">Submit decision</button>
        </form>
      ) : null}

      {confirming ? (
        <div role="dialog" aria-label="Confirm decision">
          <p>
            {decision === "REQUEST_RERECORD"
              ? "The applicant will be asked to record a new video. Continue?"
              : "This sends the applicant a text message with your decision and can't be undone. Continue?"}
          </p>
          <button type="button" onClick={submit}>Confirm</button>
          <button type="button" onClick={() => setConfirming(false)}>Cancel</button>
        </div>
      ) : null}
    </div>
  );
}

/** Small, accessible badge for the queue: shows whether automated checks flagged the application. */
function FlagBadge({ summary }: { summary?: string | null }): ReactElement {
  if (summary === "review") return <span role="status" aria-label="Flagged for review">🚩 review</span>;
  if (summary === "clear") return <span aria-label="No flags">clear</span>;
  return <span aria-label="Flags not yet computed">—</span>;
}

/** Format a millisecond offset into the video as m:ss for the moderation timestamps. */
function fmtOffset(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Reviewer flags panel (feat/admissions-review-flags). Plain-language, accessible rendering of the
 * automated checks. These are ADVISORY only - they never approve/reject. No face/appearance analysis.
 */
function FlagsPanel({ flags }: { flags?: ApplicationFlags | null }): ReactElement {
  if (!flags) {
    return (
      <section aria-label="Automated review flags">
        <h3>Automated checks</h3>
        <p>(not computed yet)</p>
      </section>
    );
  }
  const rows: Array<{ key: string; tone: "flag" | "ok" | "error"; text: string }> = [];

  const lv = flags.liveness;
  if (lv && lv.status !== "disabled") {
    if (lv.status === "error") rows.push({ key: "liveness", tone: "error", text: "Liveness check could not run (needs review)." });
    else {
      const missing = lv.missing.length ? ` (missing: ${lv.missing.join(", ")})` : "";
      rows.push({
        key: "liveness",
        tone: lv.passed ? "ok" : "flag",
        text: `Said ${lv.matched.length} of ${lv.total} check words${missing}.`,
      });
    }
  }

  const mod = flags.moderation;
  if (mod && mod.status !== "disabled") {
    if (mod.status === "error") rows.push({ key: "moderation", tone: "error", text: "Content moderation could not run (needs review)." });
    else if (mod.status === "flagged")
      for (const l of mod.labels)
        rows.push({
          key: `mod-${l.name}`,
          tone: "flag",
          text: `Possible ${l.name}${l.parent ? ` (${l.parent})` : ""} in frame at ${fmtOffset(l.firstTimestampMs)} (${Math.round(l.confidence)}%).`,
        });
    else rows.push({ key: "moderation", tone: "ok", text: "No prohibited content detected in frame." });
  }

  const kw = flags.keywords;
  if (kw && kw.status !== "disabled") {
    if (kw.status === "error") rows.push({ key: "keywords", tone: "error", text: "Keyword check could not run (needs review)." });
    else if (kw.status === "flagged") {
      const mentions = kw.matches.flatMap((m) => m.phrases).join(", ");
      rows.push({ key: "keywords", tone: "flag", text: `Mentions: ${mentions}.` });
    } else rows.push({ key: "keywords", tone: "ok", text: "No scam / off-platform keywords." });
  }

  const si = flags.silence;
  if (si && si.status !== "disabled") {
    if (si.status === "error") rows.push({ key: "silence", tone: "error", text: "Silence check could not run (needs review)." });
    else if (si.status === "flagged") {
      const pct = typeof si.speakingRatio === "number" ? `${Math.round(si.speakingRatio * 100)}% speaking time` : "very little speech";
      const words = typeof si.words === "number" ? `, ${si.words} words` : "";
      rows.push({ key: "silence", tone: "flag", text: `Mostly silent: ${pct}${words}.` });
    } else rows.push({ key: "silence", tone: "ok", text: "Enough speech / effort." });
  }

  const or = flags.origin;
  if (or && or.status !== "disabled") {
    if (or.status === "error") rows.push({ key: "origin", tone: "error", text: "Upload-origin check could not run (needs review)." });
    else if (or.status === "flagged")
      rows.push({ key: "origin", tone: "flag", text: "Video was created outside the challenge time window." });
    else rows.push({ key: "origin", tone: "ok", text: "Upload time looks consistent with the challenge." });
  }

  return (
    <section aria-label="Automated review flags">
      <h3>
        Automated checks:{" "}
        <strong>{flags.summary === "review" ? "needs review" : "clear"}</strong>
      </h3>
      <p><small>Advisory signals for the reviewer. They never approve or reject, and include no face, appearance or emotion analysis.</small></p>
      {rows.length === 0 ? (
        <p>(no checks ran)</p>
      ) : (
        <ul>
          {rows.map((r) => (
            <li key={r.key}>
              <span aria-hidden="true">{r.tone === "flag" ? "🚩 " : r.tone === "error" ? "⚠ " : "✓ "}</span>
              {r.text}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Admin surface for privacy requests (feature.yaml `privacy-withdraw-training`, permission `privacy`). */
const PRIVACY_SURFACE = "privacy-withdraw-training";

interface WithdrawResult {
  status: string;
  applications: number;
  datasetObjectsDeleted: number;
  datasetObjectsPending: number;
}

/**
 * Privacy officer form: act on an emailed "stop using my video for training" request. Finds the
 * applicant by application ID or phone number, withdraws their training consent and deletes their
 * training records. Confirm step first: it cannot be undone. The portal audit log records who did it.
 */
export function PrivacyRequestForm({ call }: Pick<FeatureScreenProps, "call">): ReactElement {
  const [by, setBy] = useState<"applicationId" | "phoneNumber">("applicationId");
  const [value, setValue] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<WithdrawResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const trimmed = value.trim();

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const r = (await call(PRIVACY_SURFACE, "POST", { [by]: trimmed })) as WithdrawResult;
      setResult(r);
      setValue("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "The request failed");
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <section aria-labelledby="adm-privacy-title">
      <h2 id="adm-privacy-title">Privacy request: stop training on an applicant&apos;s video</h2>
      <p>
        Use this when someone emails asking OneHook to stop using their application video for training Admissions
        Assist (their right under DPDP / GDPR). It withdraws their training consent on every application from the same
        phone number and deletes their video and transcript from the training data. Their application itself and its
        review decision are not changed.
      </p>
      <p>
        Check first that the email really comes from the applicant. This cannot be undone. Your name, the time and the
        result are recorded in the portal audit log.
      </p>
      <fieldset disabled={busy}>
        <legend>Find the applicant by</legend>
        <label>
          <input type="radio" name="privacy-by" checked={by === "applicationId"} onChange={() => setBy("applicationId")} />
          Application ID
        </label>
        <label>
          <input type="radio" name="privacy-by" checked={by === "phoneNumber"} onChange={() => setBy("phoneNumber")} />
          Phone number
        </label>
        <label>
          {by === "applicationId" ? "Application ID" : "Phone number (with country code, e.g. +91...)"}
          <input
            type="text"
            value={value}
            onChange={(e) => { setValue(e.target.value); setResult(null); setConfirming(false); }}
            aria-describedby="adm-privacy-hint"
          />
        </label>
        <small id="adm-privacy-hint">
          {by === "applicationId"
            ? "Shown in the applicant's email or in the Queue."
            : "The number they applied with. Every application from this number is covered."}
        </small>
        {!confirming ? (
          <button type="button" disabled={trimmed.length === 0} onClick={() => setConfirming(true)}>
            Withdraw from training
          </button>
        ) : (
          <div role="group" aria-label="Confirm withdrawal">
            <p>
              Withdraw training consent and delete training data for{" "}
              {by === "applicationId" ? "application" : "phone number"} <strong>{trimmed}</strong>? This cannot be undone.
            </p>
            <button type="button" onClick={() => void submit()}>Yes, withdraw</button>
            <button type="button" onClick={() => setConfirming(false)}>Cancel</button>
          </div>
        )}
      </fieldset>
      {error ? <p role="alert">{error}</p> : null}
      {result ? (
        <div role="status">
          <p><strong>Done.</strong> Training consent withdrawn on {result.applications} application{result.applications === 1 ? "" : "s"}.</p>
          <p>
            Training files deleted: {result.datasetObjectsDeleted}.
            {result.datasetObjectsPending > 0
              ? ` ${result.datasetObjectsPending} more will be deleted automatically shortly; nothing else to do.`
              : ""}
          </p>
          <p>You can now reply to the applicant that their video is no longer used for training.</p>
        </div>
      ) : null}
    </section>
  );
}

export default AdmissionsScreen;